import type { AttributeWidgetSource } from '@neptune/shared/widgets-domain';

import type { JsonObject } from './vega-lite-json';
import validationMetadata from './vega-lite-validation-metadata.json';

export const VEGA_LITE_FLOAT_SERIES_LIMITS = {
  maxRuns: 10,
  maxY: 20,
  maxSeries: 100,
  maxDependencies: 64,
  maxExpressionInputs: 16,
  maxExpressionBytes: validationMetadata.floatSeries.maxExpressionBytes,
  maxConcurrency: 2,
  deadlineMs: 30_000,
  maxPoints: 50_000,
  maxRows: 10_000,
  maxUniqueX: 1000,
} as const;

export type FloatSeriesAttributeRef = { name: string; type: 'floatSeries' };
export type FloatSeriesSelector =
  | { kind: 'attribute'; attribute: FloatSeriesAttributeRef }
  | { kind: 'expression'; expression: string };
export type FloatSeriesX =
  | { kind: 'step' | 'relativeTime' | 'absoluteTime' }
  | (FloatSeriesSelector & { alias?: string });
export type FloatSeriesY = FloatSeriesSelector & { as: string; alias?: string };
export type FloatSeriesRange = { from?: number; to?: number };
export type VegaLiteFloatSeriesQuery = {
  kind: 'floatSeries';
  x: FloatSeriesX;
  y: FloatSeriesY[];
  view: { xRange?: FloatSeriesRange; stepRange?: FloatSeriesRange };
};
export type VegaLiteFloatSeriesOptionsV2 = {
  version: 2;
  query: VegaLiteFloatSeriesQuery;
  dataFormat: 'wide' | 'long';
  groupBy: [];
  spec: JsonObject;
};
export type VegaLiteFloatSeriesCreationDocumentV2 = VegaLiteFloatSeriesOptionsV2;
export type FloatSeriesDependencySource = AttributeWidgetSource & {
  metadata: { attributeType: 'floatSeries' };
};
export type FloatSeriesConfigurationIssue = { path: string; message: string };
export type CompiledFloatExpression = {
  formula: string;
  attributes: string[];
  /** Numeric run attributes read by `attr(...)` in a custom X expression. */
  runAttributes?: string[];
  /** Defaults of runAttributes by index from `attr(..., default)`; null when there is no default. */
  runAttributeDefaults?: (number | null)[];
};
export type CompiledFloatSeriesY = {
  as: string;
  alias?: string;
  kind: 'attribute' | 'expression';
  name: string;
  metricKey: string;
  signature: string;
  expression: CompiledFloatExpression;
  includePreview: boolean;
};
export type CompiledFloatSeriesQuery = {
  x: FloatSeriesX;
  xExpression?: CompiledFloatExpression;
  xDependencies: string[];
  y: CompiledFloatSeriesY[];
  sources: FloatSeriesDependencySource[];
  view: VegaLiteFloatSeriesQuery['view'];
};
export type FloatSeriesCompileResult =
  | { status: 'valid'; query: CompiledFloatSeriesQuery }
  | { status: 'invalid'; issues: FloatSeriesConfigurationIssue[] };

export type FloatViewBucket = {
  bucketNo: number;
  minY: number | null;
  maxY: number | null;
  sum: number | null;
  count: number;
  positiveInfCount: number;
  negativeInfCount: number;
  nanCount: number;
};
export type FloatNonFinite = 'NaN' | 'Infinity' | '-Infinity' | 'mixed' | 'unknown';
export type FloatViewPoint = {
  x: number;
  pointY: number | null;
  pointNonFinite: FloatNonFinite | null;
  step: number | null;
  timestampMillis: number | null;
  interpolation: boolean;
  bucket: FloatViewBucket | null;
};
export type FloatCell = {
  value: number | null;
  present: boolean;
  nonFinite: FloatNonFinite | null;
  valueKind: 'bucketMean' | 'point' | 'missing' | 'nonFinite';
  pointY: number | null;
  step: number | null;
  timestampMillis: number | null;
  interpolation: boolean | null;
  bucketNo: number | null;
  minY: number | null;
  maxY: number | null;
  sum: number | null;
  count: number | null;
  positiveInfCount: number | null;
  negativeInfCount: number | null;
  nanCount: number | null;
};
